import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function EconomicDataAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Economic Data API" description="The Economic Data API provides access to datasets. This data is essential for macroeconomic analysis, forecasting, and understanding market-moving economic indicators across global markets."  canonicalUrl="https://axionquant.com/docs/04-macros-and-signals/economic-datasets/00-search" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Economic Data API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Economic Data API provides access to datasets. This data is essential for macroeconomic analysis, forecasting, and understanding market-moving economic indicators across global markets.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Find Economic Series */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /econ/find
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Natural language search for economic time series. Describe the dataset you need in plain English, and the endpoint translates your query into relevant FRED search terms, returning matching series metadata. Ideal for discovery when you don&apos;t know the exact series name.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">query</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Natural language description of the economic data you&apos;re looking for (e.g., &quot;Semiconductor spending&quot;, &quot;Housing starts in California&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/econ/find?query=GDP', '/econ/find?query=Unemployment+rate']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <p className="text-sm text-gray-600 mb-4">
                Returns an array of series objects with the same fields as the <code className="text-xs bg-gray-100 px-1 rounded">/econ/search</code> endpoint. Results are automatically deduplicated across the generated search terms.
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Series ID</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">realtimeStart <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Start of real-time period (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">realtimeEnd <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">End of real-time period (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">title <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full series title</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">observationStart <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">First observation date (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">observationEnd <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Last observation date (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">frequency <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full frequency description</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">frequencyShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Abbreviated frequency code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">units <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Measurement units</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">unitsShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Abbreviated units</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">seasonalAdjustment <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Seasonal adjustment method</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">seasonalAdjustmentShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Short seasonal adjustment code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">lastUpdated <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Last update timestamp</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">popularity <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Popularity score (higher = more accessed)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">notes <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Detailed description and source information</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Find Economic Series" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Find Economic Series</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="econ/find"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/economic-datasets/find"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* Search Economic Series */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /econ/search
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Searches for economic time series by keyword or description. Returns metadata for matching series including frequency, units, and coverage period. This endpoint is typically used to discover series IDs for data retrieval.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Query Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">query</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Search term for economic series (e.g., &quot;Corn prices&quot;, &quot;GDP&quot;, &quot;Unemployment&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <Links links={['/econ/search?query=GDP', '/econ/search?query=Unemployment&limit=50']} />
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Series ID</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">realtimeStart <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Start of real-time period (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">realtimeEnd <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">End of real-time period (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">title <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full series title</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">observationStart <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">First observation date (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">observationEnd <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Last observation date (YYYY-MM-DD)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">frequency <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Full frequency description</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">frequencyShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Abbreviated frequency code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">units <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Measurement units</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">unitsShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Abbreviated units</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">seasonalAdjustment <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Seasonal adjustment method</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">seasonalAdjustmentShort <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Short seasonal adjustment code</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">lastUpdated <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Last update timestamp</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">popularity <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Popularity score (higher = more accessed)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">notes <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Detailed description and source information</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Search Economic Series" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Search Economic Series</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="econ/search"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="04-macros-and-signals/economic-datasets/00-search"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
